import type { IMenuProps, IOmit } from '@sixui/ui';
import { faTrashCan, faWandSparkles } from '@fortawesome/pro-light-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUserBadge } from '~/lib/api/modules/UserBadges';
import { useDeleteUserBadgeMutation } from '~/lib/api/modules/UserBadges/hooks/useDeleteUserBadgeMutation';
import { useUpdateUserBadgeMutation } from '~/lib/api/modules/UserBadges/hooks/useUpdateUserBadgeMutation';

export type IUserBadgeListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    userBadge: IApiUserBadge;
  };

export const UserBadgeListItemMenu: React.FC<IUserBadgeListItemMenuProps> = (
  props,
) => {
  const { sx, userBadge, ...other } = props;
  const { t } = useTranslation();
  const deleteUserBadgeMutation = useDeleteUserBadgeMutation();
  const updateUserBadgeMutation = useUpdateUserBadgeMutation();
  const processing =
    deleteUserBadgeMutation.isLoading || updateUserBadgeMutation.isLoading;

  const handleDelete = (): void => {
    if (deleteUserBadgeMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(
      t('UserBadgeListItem.dialog.confirmDeletion.text', {
        name: userBadge.name,
      }),
    );

    if (doDeletion) {
      void deleteUserBadgeMutation
        .mutateAsync({
          appId: userBadge.appId,
          userBadgeId: userBadge._id,
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  };

  const handleToggleDefault = (): void => {
    if (updateUserBadgeMutation.isLoading) {
      return;
    }

    void updateUserBadgeMutation.mutateAsync({
      appId: userBadge.appId,
      userBadgeId: userBadge._id,
      body: {
        access: userBadge.access,
        color: userBadge.color,
        description: userBadge.description,
        isDefault: !userBadge.isDefault,
        management: userBadge.management,
        name: userBadge.name,
        privacyPolicyUrl: userBadge.privacyPolicyUrl,
        productId: userBadge.productId ?? undefined,
        subscriptionUrl: userBadge.subscriptionUrl,
      },
    });
  };

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      {handleToggleDefault ? (
        <MenuItem
          label={
            userBadge.isDefault
              ? t('UserBadgeListItemMenu.option.disableDefault.label')
              : t('UserBadgeListItemMenu.option.enableDefault.label')
          }
          leadingIcon={<FontAwesomeIcon icon={faWandSparkles} />}
          onClick={handleToggleDefault}
        />
      ) : null}
      {handleDelete ? (
        <MenuItem
          label={t('common.delete')}
          leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
          onClick={handleDelete}
          variant="danger"
        />
      ) : null}
    </Menu>
  );
};
